import { useState, useEffect, useRef } from "react";
import { SketchPicker } from "react-color";
import "./StatusColorForm.css";
import "./Forms.css";

const UNIT_STATES = [
    'disponible',
    'reservado',
    'vendido',
    'opcionado',
    'bloqueado',
];

const STATE_TRANSLATIONS = {
    'disponible': 'available',
    'reservado': 'reserved',
    'vendido': 'sold',
    'opcionado': 'optioned',
    'bloqueado': 'blocked'
};

const defaultColors = {
    disponible: '#00ff00',
    reservado: '#ffa500',
    vendido: '#ff0000',
    opcionado: '#1b84f4',
    bloqueado: '#808080',
};

const StatusColorForm = ({ initialColors, onSave, onClose }) => {
    const [colors, setColors] = useState({});
    const [hexInputs, setHexInputs] = useState({});
    const [activePicker, setActivePicker] = useState(null);
    const pickerRef = useRef();

    useEffect(() => {
        function handleClickOutside(event) {
            if (
                pickerRef.current &&
                !pickerRef.current.contains(event.target)
            ) {
                setActivePicker(null);
            }
        }

        if (activePicker) {
            document.addEventListener("mousedown", handleClickOutside);
        } else {
            document.removeEventListener("mousedown", handleClickOutside);
        }

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [activePicker]);


    useEffect(() => {
        const initialHexInputs = {};
        const initialColorObjs = {};

        UNIT_STATES.forEach(status => {
            const englishStatus = STATE_TRANSLATIONS[status] || status;
            const savedColor = initialColors?.[englishStatus] || initialColors?.[status];

            initialHexInputs[status] = savedColor || defaultColors[status];
            initialColorObjs[status] = savedColor ? hexToHsv(savedColor) : hexToHsv(defaultColors[status]);
        });

        setHexInputs(initialHexInputs);
        setColors(initialColorObjs);
    }, [initialColors]);

    const handleColorChange = (status, color) => {
        setColors(prev => ({
            ...prev,
            [status]: color.hsv,
        }));
        setHexInputs(prev => ({
            ...prev,
            [status]: color.hex,
        }));
    };

    const handleHexInputChange = (status, value) => {
        let hexValue = value;
        if (hexValue && !hexValue.startsWith('#')) {
            hexValue = '#' + hexValue.replace(/^#*/, '');
        }
        setHexInputs(prev => ({
            ...prev,
            [status]: hexValue,
        }));

        if (/^#[0-9A-Fa-f]{6}$/.test(hexValue)) {
            const hsv = hexToHsv(hexValue);
            setColors(prev => ({
                ...prev,
                [status]: hsv,
            }));
        }
    };

    const handleSave = () => {
        const translatedColors = {};
        Object.keys(hexInputs).forEach(key => {
            const englishKey = STATE_TRANSLATIONS[key] || key;
            translatedColors[englishKey] = hexInputs[key];
        });

        onSave(translatedColors);
        if (onClose) onClose();
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            setActivePicker(null);
        }
    };

    function hexToHsv(hex) {
        let r = 0, g = 0, b = 0;
        if (hex.length === 7) {
            r = parseInt(hex.slice(1, 3), 16) / 255;
            g = parseInt(hex.slice(3, 5), 16) / 255;
            b = parseInt(hex.slice(5, 7), 16) / 255;
        }
        const max = Math.max(r, g, b), min = Math.min(r, g, b);
        const d = max - min;
        let h = 0, s = max === 0 ? 0 : d / max, v = max;

        if (max !== min) {
            switch (max) {
                case r: h = (g - b) / d + (g < b ? 6 : 0); break;
                case g: h = (b - r) / d + 2; break;
                case b: h = (r - g) / d + 4; break;
                default: break;
            }
            h *= 60;
        }

        return { h, s, v };
    }

    return (
        <div>
            <h2>Asignar colores por estado</h2>
            {colors && (<div></div>)}
            {UNIT_STATES.map(status => {
                const currentHex = hexInputs[status] || defaultColors[status];
                return (
                    <div key={status} style={{ marginBottom: 20 }}>
                        <label><strong>{status.charAt(0).toUpperCase() + status.slice(1)}</strong></label>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                            <div
                                style={{
                                    width: 36,
                                    height: 36,
                                    backgroundColor: currentHex,
                                    border: '1px solid #ccc',
                                    cursor: 'pointer',
                                }}
                                onClick={() => setActivePicker(activePicker === status ? null : status)}
                            />
                            {activePicker === status && (
                                <div
                                    ref={pickerRef}
                                    style={{ position: 'absolute', zIndex: 2 }}
                                    tabIndex={0}
                                    onKeyDown={handleKeyDown}
                                >
                                    <SketchPicker
                                        color={currentHex}
                                        onChange={(color) => handleColorChange(status, color)}
                                    />
                                </div>
                            )}
                            <input
                                type="text"
                                value={currentHex}
                                onChange={(e) => handleHexInputChange(status, e.target.value)}
                                style={{ width: '19vw', textAlign: 'center' }}
                                placeholder="Selecciona un color"
                            />
                        </div>
                    </div>
                );
            })}

            <button onClick={handleSave} className="btn-submit">
                Guardar colores
            </button>
        </div>
    );
};

export default StatusColorForm;